// FlexibilityOptions — the "options & flexibility" lead-in that sits between
// the Lifecycle covers and Pricing: custom edition register → upgrade-anytime
// → multiple-births offers (the last option before pricing).
import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { UpgradePanel } from "./UpgradePanel";

const FADE = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } };

function CustomEditionCard() {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const [email, setEmail] = useState("");
    const [journey, setJourney] = useState("");
    const [done, setDone] = useState(false);

    async function reserve(e) {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await api.post("/orders/interest", { plan: "custom", email, quantity: 1, notes: journey.trim() || null });
            setDone(true);
            toast.success(t("pricing.reserved_toast"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your interest. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <motion.div
            {...FADE}
            className="rounded-[2rem] bg-white border border-[#E5E1D8]/80 p-8 sm:p-12 shadow-rem-soft grid grid-cols-1 md:grid-cols-2 gap-8 items-center"
            data-testid="custom-edition-card"
        >
            <div>
                <div className="flex items-center gap-2 mb-4">
                    <ForgetMeNotIcon size={22} />
                    <p className="font-sans text-[11px] uppercase tracking-[0.22em] font-semibold text-[#6B7DB8]">
                        {t("pricing.custom.eyebrow")}
                    </p>
                </div>
                <h3 className="font-serif text-3xl lg:text-4xl text-[#2D2B2A] mb-3">{t("pricing.custom.title")}</h3>
                <p className="text-sm text-[#5C5A59] leading-relaxed mb-3">{t("pricing.custom.lead")}</p>
                <p className="text-xs text-[#8A8786] italic">{t("pricing.custom.note")}</p>
            </div>
            {done ? (
                <div className="rounded-2xl p-5 text-sm font-medium bg-[#F4F6FB] text-[#4A5A95]" data-testid="plan-reserved-custom">
                    {t("pricing.reserved")}
                </div>
            ) : (
                <form onSubmit={reserve} className="space-y-3" data-testid="plan-form-custom">
                    <input
                        type="text"
                        placeholder={t("pricing.custom.journey_placeholder")}
                        value={journey}
                        onChange={(e) => setJourney(e.target.value)}
                        className="w-full px-5 py-3 rounded-full text-sm outline-none transition-all bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB]"
                        data-testid="custom-journey-input"
                    />
                    <input
                        type="email"
                        required
                        placeholder={t("pricing.form_email_placeholder")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-5 py-3 rounded-full text-sm outline-none transition-all bg-[#F4F6FB] text-[#2D2B2A] placeholder-[#8A8786] border border-[#E5E1D8] focus:border-[#8C9ECB]"
                        data-testid="custom-email-input"
                    />
                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all hover:-translate-y-0.5 disabled:opacity-60 bg-[#6B7DB8] hover:bg-[#4A5A95] text-white"
                        data-testid="reserve-custom-btn"
                    >
                        {submitting ? t("pricing.form_submitting") : t("pricing.form_submit")}
                        <ArrowRight weight="bold" size={16} />
                    </button>
                </form>
            )}
        </motion.div>
    );
}

function MultiplesPanel() {
    const { t } = useTranslation();
    return (
        <motion.div
            {...FADE}
            transition={{ ...FADE.transition, delay: 0.15 }}
            className="mt-14 rounded-[2rem] bg-gradient-to-br from-[#FBF1F2] via-white to-[#EAF2F9] border border-[#F4C7CC]/40 p-8 sm:p-12 shadow-rem-soft"
            data-testid="pricing-multiples"
        >
            <p className="font-sans text-xs uppercase tracking-[0.28em] text-[#A85D62] font-semibold mb-5 text-center">
                {t("pricing.multiples.eyebrow")}
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#2D2B2A] leading-tight text-center">
                {t("pricing.multiples.headline_part1")}{" "}
                <em className="italic text-[#A85D62]">
                    {t("pricing.multiples.headline_emphasis")}
                </em>
            </h3>
            <p className="mt-5 text-[#5C5A59] leading-relaxed text-center max-w-2xl mx-auto">
                {t("pricing.multiples.lead")}
            </p>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto">
                {(t("pricing.multiples.tiers", { returnObjects: true }) || []).map((tier, i) => (
                    <div
                        key={tier.k}
                        className="rounded-2xl bg-white border border-[#E5E1D8] p-6 text-center"
                        data-testid={`multiples-tier-${i + 1}`}
                    >
                        <p className="font-sans text-[10px] uppercase tracking-[0.26em] text-[#A85D62] font-semibold mb-2">
                            {tier.k}
                        </p>
                        <p className="font-serif italic text-lg text-[#2D2B2A] leading-snug">
                            {tier.offer}
                        </p>
                    </div>
                ))}
            </div>
            <p className="mt-7 text-center text-xs text-[#8A8786] italic max-w-xl mx-auto">
                {t("pricing.multiples.footnote")}
            </p>
        </motion.div>
    );
}

export function FlexibilityOptions() {
    return (
        <section
            className="relative py-24 px-6 bg-[#FAF7F2] overflow-hidden"
            data-testid="flexibility-options-section"
            id="flexibility"
        >
            <div className="max-w-6xl mx-auto">
                <CustomEditionCard />
                <UpgradePanel />
                <MultiplesPanel />
            </div>
        </section>
    );
}
